import {
  getDbqContentionDisplayLabel,
  parseStoredDbqContentions,
} from './dbqConditionOptions';
import {
  getFileFolderLabel,
  getFileFolderPath,
} from '../../fileBrowser/fileFolderUtils';
import {
  getChangesRequestedStatusLabel,
} from '../../../lib/dbqWorkflow';

const normalizeTextValue = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const parseFileMetaValue = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (err) {
      return {};
    }
  }
  return typeof value === 'object' ? value : {};
};

const DD214_FILE_NAME_PATTERN = /(?:^|[^a-z0-9])dd[\s_-]?214(?:[^a-z0-9]|$)/i;

const countNestedEntries = (value, predicate = () => true) => {
  if (!value || typeof value !== 'object') return 0;

  return Object.values(value).reduce((total, entries) => {
    if (!Array.isArray(entries)) return total;
    return total + entries.filter(predicate).length;
  }, 0);
};

const flattenNestedEntries = (value, predicate = () => true) => {
  if (!value || typeof value !== 'object') return [];

  return Object.values(value).flatMap((entries) => {
    if (!Array.isArray(entries)) return [];
    return entries.filter(predicate);
  });
};

const buildSuggestionStats = (suggestions = []) =>
  suggestions.reduce((stats, suggestion) => {
    const status = String(suggestion?.status || 'pending').toLowerCase();

    stats.total += 1;
    if (status === 'accepted') stats.accepted += 1;
    else if (status === 'rejected') stats.rejected += 1;
    else if (status === 'dismissed') stats.dismissed += 1;
    else stats.pending += 1;

    return stats;
  }, {
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    dismissed: 0,
  });

export const DBQ_AI_AUTOMATED_FIELD_CONFIGS = Object.freeze([
  {
    key: 'evidence_review',
    label: 'Evidence Review',
    displayOrder: 10,
    description:
      'Summarizes the records reviewed for the DBQ evidence section.',
    promptPlaceholder:
      'Optional guidance for how the evidence review should be written.',
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'medical_history',
    label: 'Medical History',
    displayOrder: 20,
    description:
      'Drafts the DBQ-specific medical history fields using the available claim context.',
    promptPlaceholder:
      'Optional guidance for medical history answers on this request.',
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'musculoskeletal_flareups',
    label: 'Flare-Ups',
    displayOrder: 30,
    description:
      'Drafts the flare-ups narrative for musculoskeletal DBQs when the field is present.',
    promptPlaceholder:
      'Optional guidance for musculoskeletal flare-up descriptions.',
    requiresMusculoskeletal: true,
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'musculoskeletal_functional_loss',
    label: 'Functional Loss',
    displayOrder: 40,
    description:
      'Drafts the repeated-use or functional-loss narrative for musculoskeletal DBQs.',
    promptPlaceholder:
      'Optional guidance for musculoskeletal functional loss descriptions.',
    requiresMusculoskeletal: true,
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'diagnostic_testing_results',
    label: 'Diagnostic Testing Results',
    displayOrder: 70,
    description:
      'Drafts the abnormal-test-results relationship field under Diagnostic Testing.',
    promptPlaceholder:
      'Optional guidance for diagnostic testing results.',
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'diagnostic_testing_other_findings',
    label: 'Diagnostic Testing Other Findings',
    displayOrder: 80,
    description:
      'Drafts the other significant or clinically relevant diagnostic findings field.',
    promptPlaceholder:
      'Optional guidance for other diagnostic testing findings.',
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'functional_impact',
    label: 'Functional Impact',
    displayOrder: 90,
    description:
      'Drafts the functional impact answer when that field exists on the selected DBQ.',
    promptPlaceholder:
      'Optional guidance for describing occupational and daily-life impact.',
    requiresExpandedAiAutomatedFields: true,
  },
  {
    key: 'medical_opinion',
    label: 'Medical Opinion',
    displayOrder: 100,
    description:
      'Creates the medical opinion draft for the selected contention and TOE.',
    promptPlaceholder:
      'Optional guidance for how the medical opinion should be written.',
  },
]);

export const filterAiAutomatedFieldConfigsByAccess = (
  configs = DBQ_AI_AUTOMATED_FIELD_CONFIGS,
  {
    expandedAiAutomatedFieldsEnabled = false,
    isSelectedDbqMusculoskeletal = false,
  } = {},
) =>
  configs
    .filter((config) =>
      !config.requiresExpandedAiAutomatedFields || expandedAiAutomatedFieldsEnabled)
    .filter((config) => !config.requiresMusculoskeletal || isSelectedDbqMusculoskeletal)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

export const buildDefaultAiAutomatedFields = () =>
  DBQ_AI_AUTOMATED_FIELD_CONFIGS.reduce((fields, config) => {
    fields[config.key] = {
      enabled: true,
      prompt: '',
    };
    return fields;
  }, {});

export const normalizeAiAutomatedFields = (value = {}, legacy = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  return DBQ_AI_AUTOMATED_FIELD_CONFIGS.reduce((fields, config) => {
    const current =
      source[config.key] && typeof source[config.key] === 'object' && !Array.isArray(source[config.key])
        ? source[config.key]
        : {};
    const legacyPrompt =
      config.key === 'medical_opinion'
        ? legacy.moPrompt
        : config.key === 'evidence_review'
          ? legacy.evidenceReviewPrompt
          : legacy.aiFieldPrompts?.[config.key];
    const legacyEnabled =
      config.key === 'medical_opinion' && typeof legacy.autoGenerateMedicalOpinion === 'boolean'
        ? legacy.autoGenerateMedicalOpinion
        : undefined;

    fields[config.key] = {
      enabled:
        typeof current.enabled === 'boolean'
          ? current.enabled
          : typeof legacyEnabled === 'boolean'
            ? legacyEnabled
            : true,
      prompt:
        typeof current.prompt === 'string'
          ? current.prompt
          : typeof legacyPrompt === 'string'
            ? legacyPrompt
            : '',
    };
    return fields;
  }, {});
};

export const filterAiAutomatedFieldsByAccess = (
  fields = {},
  {
    expandedAiAutomatedFieldsEnabled = false,
  } = {},
) => {
  const normalizedFields = normalizeAiAutomatedFields(fields);
  return DBQ_AI_AUTOMATED_FIELD_CONFIGS.reduce((filteredFields, config) => {
    if (config.requiresExpandedAiAutomatedFields && !expandedAiAutomatedFieldsEnabled) {
      return filteredFields;
    }

    filteredFields[config.key] = normalizedFields[config.key];
    return filteredFields;
  }, {});
};

export const EMPTY_DBQ_REQUEST_DRAFT = Object.freeze({
  userDbqId: null,
  dbqFormId: '',
  dbqStableFormId: '',
  dbqShortName: '',
  dbqTitle: '',
  requestStatus: '',
  toeId: '',
  selectedContentions: [],
  requestContentions: [],
  requestNotes: '',
  providerUserId: '',
  selectedEvidenceFileIds: [],
  evidenceReviewPrompt: '',
  autoGenerateMedicalOpinion: true,
  moPrompt: '',
  aiAutomatedFields: buildDefaultAiAutomatedFields(),
  createPdf: false,
  insertIntoDbq: true,
});

export const isDd214EvidenceFile = (file = {}) => {
  const fileMeta = parseFileMetaValue(file?.fileMeta);
  const docType = normalizeTextValue(fileMeta?.doc_type || fileMeta?.docType).toLowerCase();
  const fileName = normalizeTextValue(file?.fileName);

  if (
    docType === 'dd214' ||
    docType === 'dd-214' ||
    docType.includes('dd214') ||
    docType.includes('dd 214')
  ) {
    return true;
  }

  return DD214_FILE_NAME_PATTERN.test(fileName);
};

export const getDd214EvidenceFiles = (files = []) =>
  toArray(files).filter((file) => isDd214EvidenceFile(file));

export const getPreferredDd214EvidenceFile = (files = []) =>
  getDd214EvidenceFiles(files)[0] || null;

export const ensureDd214SelectedEvidenceFileIds = (
  selectedEvidenceFileIds = [],
  dd214FileId = null,
) => {
  const normalizedSelectedFileIds = toArray(selectedEvidenceFileIds).filter(Boolean);
  if (!dd214FileId) return normalizedSelectedFileIds;
  if (normalizedSelectedFileIds.includes(dd214FileId)) return normalizedSelectedFileIds;
  return [...normalizedSelectedFileIds, dd214FileId];
};

export const buildDd214EvidenceWarning = ({
  claimEvidenceFiles = [],
  selectedEvidenceFileIds = [],
} = {}) => {
  const dd214Files = getDd214EvidenceFiles(claimEvidenceFiles);
  if (dd214Files.length === 0) {
    return 'DD-214 was not found in the claim files. Add it to the claim before sending the request if it is available.';
  }

  const selectedFileIdSet = new Set(toArray(selectedEvidenceFileIds));
  const selectedDd214File = dd214Files.find((file) => selectedFileIdSet.has(file.fileId));
  if (selectedDd214File) {
    return null;
  }

  return `DD-214 is available but not included in this evidence package. Add "${dd214Files[0].fileName}" unless there is a specific reason to exclude it.`;
};

export const buildDbqRequestDraftFromUserDbq = (userDbq) => {
  const request = userDbq?.request || {};
  const medicalOpinionParameters = request.medicalOpinionParameters || {};
  const aiFieldGenerationState =
    userDbq?.formData?._aiFieldGeneration &&
    typeof userDbq.formData._aiFieldGeneration === 'object' &&
    !Array.isArray(userDbq.formData._aiFieldGeneration)
      ? userDbq.formData._aiFieldGeneration
      : {};
  const aiFieldPrompts = DBQ_AI_AUTOMATED_FIELD_CONFIGS.reduce((prompts, config) => {
    if (config.key === 'medical_opinion') return prompts;
    const requestPrompt = aiFieldGenerationState?.[config.key]?.requestPrompt;
    if (typeof requestPrompt === 'string') prompts[config.key] = requestPrompt;
    return prompts;
  }, {});
  const evidenceReviewRequestPrompt = aiFieldPrompts.evidence_review || '';
  const aiAutomatedFields = normalizeAiAutomatedFields(
    DBQ_AI_AUTOMATED_FIELD_CONFIGS.reduce((fields, config) => {
      const state = aiFieldGenerationState?.[config.key];
      if (state && typeof state === 'object' && !Array.isArray(state)) {
        fields[config.key] = {
          enabled:
            typeof state.generationEnabled === 'boolean'
              ? state.generationEnabled
              : undefined,
          prompt: typeof state.requestPrompt === 'string' ? state.requestPrompt : undefined,
        };
      }
      return fields;
    }, {}),
    {
      evidenceReviewPrompt: evidenceReviewRequestPrompt,
      autoGenerateMedicalOpinion:
        typeof medicalOpinionParameters.autoGenerateMedicalOpinion === 'boolean'
          ? medicalOpinionParameters.autoGenerateMedicalOpinion
          : true,
      moPrompt: medicalOpinionParameters.prompt || '',
      aiFieldPrompts,
    },
  );
  const requestContentions = Array.isArray(request?.requestContentions)
    ? request.requestContentions
      .map((requestContention, index) => ({
        contention: requestContention?.contention || requestContention?.contentionKey || '',
        contentionLabel:
          requestContention?.contentionLabel ||
          getDbqContentionDisplayLabel(
            requestContention?.contention || requestContention?.contentionKey || '',
          ),
        toeId: requestContention?.toeId || '',
        sortOrder: Number.isInteger(Number(requestContention?.sortOrder))
          ? Number(requestContention.sortOrder)
          : index,
      }))
      .filter((requestContention) => requestContention.contention)
    : [];
  const selectedContentions = requestContentions.length > 0
    ? requestContentions.map((requestContention) => requestContention.contention)
    : parseStoredDbqContentions(request.contention);
  const fallbackToeId = request.toeId || userDbq?.toeId || '';
  const normalizedRequestContentions =
    requestContentions.length > 0
      ? requestContentions
      : selectedContentions.map((contention, index) => ({
          contention,
          contentionLabel: getDbqContentionDisplayLabel(contention),
          toeId: fallbackToeId,
          sortOrder: index,
        }));

  return {
    ...EMPTY_DBQ_REQUEST_DRAFT,
    userDbqId: userDbq?.userDbqId || null,
    dbqFormId: userDbq?.dbqFormId || '',
    dbqStableFormId: userDbq?.formId || '',
    dbqShortName: userDbq?.shortName || '',
    dbqTitle: userDbq?.title || '',
    requestStatus: request.status || '',
    toeId: fallbackToeId,
    selectedContentions,
    requestContentions: normalizedRequestContentions,
    requestNotes: request.requestNotes || '',
    providerUserId: request.providerUserId || '',
    selectedEvidenceFileIds: toArray(request.selectedEvidenceFileIds),
    evidenceReviewPrompt: evidenceReviewRequestPrompt,
    autoGenerateMedicalOpinion:
      typeof medicalOpinionParameters.autoGenerateMedicalOpinion === 'boolean'
        ? medicalOpinionParameters.autoGenerateMedicalOpinion
        : true,
    moPrompt: medicalOpinionParameters.prompt || '',
    aiAutomatedFields,
    createPdf:
      typeof medicalOpinionParameters.createPdf === 'boolean'
        ? medicalOpinionParameters.createPdf
        : false,
    insertIntoDbq:
      typeof medicalOpinionParameters.insertIntoDbq === 'boolean'
        ? medicalOpinionParameters.insertIntoDbq
        : true,
  };
};

export const getWorkspaceConditionLabels = (userDbq) => {
  const requestContentionRows = getWorkspaceRequestContentionRows(userDbq);
  if (requestContentionRows.length > 0) {
    return requestContentionRows.map((requestContention) => requestContention.contentionLabel);
  }

  return parseStoredDbqContentions(userDbq?.request?.contention).map((value) =>
    getDbqContentionDisplayLabel(value),
  );
};

export const getWorkspaceRequestContentionRows = (userDbq, toeOptionsById = new Map()) => {
  const requestContentions = Array.isArray(userDbq?.request?.requestContentions)
    ? userDbq.request.requestContentions
    : [];

  if (requestContentions.length > 0) {
    return requestContentions.map((requestContention, index) => ({
      contention:
        requestContention?.contention ||
        requestContention?.contentionKey ||
        '',
      contentionLabel:
        requestContention?.contentionLabel ||
        getDbqContentionDisplayLabel(
          requestContention?.contention || requestContention?.contentionKey || '',
        ),
      toeId: requestContention?.toeId || '',
      toeLabel: getWorkspaceToeLabel(
        toeOptionsById,
        requestContention?.toeId,
        requestContention?.toe,
      ),
      sortOrder: Number.isInteger(Number(requestContention?.sortOrder))
        ? Number(requestContention.sortOrder)
        : index,
    }));
  }

  const legacyContentions = parseStoredDbqContentions(userDbq?.request?.contention);
  const legacyToeId = userDbq?.request?.toeId || userDbq?.toeId || '';

  return legacyContentions.map((contention, index) => ({
    contention,
    contentionLabel: getDbqContentionDisplayLabel(contention),
    toeId: legacyToeId,
    toeLabel: getWorkspaceToeLabel(toeOptionsById, legacyToeId, userDbq?.request?.toe),
    sortOrder: index,
  }));
};

export const getWorkspaceToeSummary = (userDbq, toeOptionsById = new Map()) => {
  const requestContentionRows = getWorkspaceRequestContentionRows(userDbq, toeOptionsById);
  const toeLabels = [...new Set(
    requestContentionRows
      .map((requestContention) => requestContention.toeLabel)
      .filter((label) => label && label !== 'Not selected'),
  )];

  if (toeLabels.length === 0) return 'Not selected';
  if (requestContentionRows.length <= 1 || toeLabels.length === 1) return toeLabels[0];

  return `${requestContentionRows.length} TOE assignments`;
};

export const getDbqSuggestionStats = (formData = {}) =>
  buildSuggestionStats(flattenNestedEntries(formData._suggestions));

export const getDbqReviewCounts = (formData = {}) => {
  const suggestionStats = getDbqSuggestionStats(formData);

  return {
    commentCount: countNestedEntries(formData._comments),
    suggestionCount: suggestionStats.total,
    pendingSuggestionCount: suggestionStats.pending,
    suggestionStats,
  };
};

export const getPendingDbqSuggestions = (formData = {}) =>
  flattenNestedEntries(
    formData._suggestions,
    (suggestion) => suggestion?.status === 'pending',
  ).sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

export const getDbqRequestStatusMeta = (
  userDbq,
  requestStatuses = {},
  legacyStatuses = {},
) => {
  const requestStatus = userDbq?.request?.status;
  if (requestStatus && requestStatuses[requestStatus]) {
    if (requestStatus === 'changes_requested') {
      return {
        ...requestStatuses[requestStatus],
        label: getChangesRequestedStatusLabel(userDbq?.request),
      };
    }

    return requestStatuses[requestStatus];
  }

  return legacyStatuses[userDbq?.status || 'draft'] || {
    label: 'Draft',
    variant: 'gray',
  };
};

export const getWorkspaceConditionTitle = (userDbq) => {
  const labels = getWorkspaceConditionLabels(userDbq);
  if (labels.length === 1) return labels[0];
  if (labels.length > 1) return `${labels[0]} +${labels.length - 1}`;

  return normalizeTextValue(userDbq?.title) || 'Untitled request';
};

export const getWorkspaceRequestNotesPreview = (userDbq, maxLength = 400) => {
  const value = normalizeTextValue(userDbq?.request?.requestNotes);
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, maxLength).trim()}...`;
};

export const getWorkspacePersonLabel = (person, fallback = 'Unassigned') => {
  if (!person) return fallback;
  const fullName = `${person.userFirstName || ''} ${person.userLastName || ''}`.trim();
  return fullName || person.userEmail || fallback;
};

export const getWorkspaceToeLabel = (toeOptionsById, toeId, toe = null) => {
  if (!toeId) return 'Not selected';
  const toeOption = toeOptionsById.get(toeId);
  if (toeOption) return toeOption.title || toeOption.toeType || toeOption.toeId;
  if (toe && typeof toe === 'object') return toe.title || toe.toeType || toe.toeId || toeId;
  return toeId;
};

export const groupDbqEvidenceFilesByFolder = (files = []) => {
  const groups = new Map();

  files.forEach((file) => {
    const folderPath = getFileFolderPath(file);
    const currentGroup = groups.get(folderPath) || [];
    currentGroup.push(file);
    groups.set(folderPath, currentGroup);
  });

  return [...groups.entries()]
    .sort((a, b) => getFileFolderLabel(a[0]).localeCompare(getFileFolderLabel(b[0])))
    .map(([folderPath, filesInFolder]) => ({
      folderPath,
      folderLabel: getFileFolderLabel(folderPath),
      files: [...filesInFolder].sort((a, b) =>
        String(a.fileName || '').localeCompare(String(b.fileName || '')),
      ),
    }));
};
